import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  FormControlLabel,
  RadioGroup,
} from '@mui/material';
import { Quote, SupplierRate } from '../../types/quote.types';

interface SupplierSelectionModalProps {
  open: boolean;
  onClose: () => void;
  quote: Quote | null;
  onSelectSupplier: (supplier: SupplierRate) => void;
}

const SupplierSelectionModal: React.FC<SupplierSelectionModalProps> = ({
  open,
  onClose,
  quote,
  onSelectSupplier,
}) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');

  const handleSupplierSelect = (supplier: SupplierRate) => {
    setSelectedSupplierId(supplier.supplier);
  };

  const handleConfirm = () => {
    const selectedSupplier = quote?.supplierRates.find(
      supplier => supplier.supplier === selectedSupplierId
    );
    if (selectedSupplier) {
      onSelectSupplier(selectedSupplier);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount == null || !Number.isFinite(amount)) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  // Audit 13/F-156: null = no meaningful baseline → 'N/A', not a 0% saving
  const formatPercentage = (percentage: number | null | undefined) => {
    if (percentage == null || !Number.isFinite(percentage)) return 'N/A';
    const sign = percentage > 0 ? '+' : '';
    return `${sign}${percentage.toFixed(2)}%`;
  };

  const getSavingsColor = (percentage: number | null | undefined) => {
    if (percentage == null || !Number.isFinite(percentage)) return 'default';
    return percentage > 0 ? 'success' : 'error';
  };

  if (!quote) {
    return null;
  }

  // Sort suppliers by savings (best savings first; uncomparable rates last)
  const savingsOrder = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? -Infinity : v);
  const sortedSuppliers = [...quote.supplierRates].sort((a, b) => savingsOrder(b.percentageDifference) - savingsOrder(a.percentageDifference));

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '600px',
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Select Supplier
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Choose the best supplier option for your client
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Current Supplier Reference */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                  Current Supplier: {quote.currentSupplier.supplierName}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Annual Cost
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {formatCurrency(quote.currentSupplier.annualExpenditure)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Monthly Cost
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {formatCurrency(quote.currentSupplier.monthlyExpenditure)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Standing Charge
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {quote.currentSupplier.standingCharge}p/day
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Day Rate
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      {quote.currentSupplier.dayUnitRate}p/kWh
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Supplier Options */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Available Supplier Options
            </Typography>
            <RadioGroup
              value={selectedSupplierId}
              onChange={(e) => setSelectedSupplierId(e.target.value)}
            >
              <Grid container spacing={2}>
                {sortedSuppliers.map((supplier, index) => (
                  <Grid item xs={12} key={supplier.supplier}>
                    <Card 
                      variant="outlined"
                      sx={{
                        border: selectedSupplierId === supplier.supplier ? '2px solid' : '1px solid',
                        borderColor: selectedSupplierId === supplier.supplier ? 'primary.main' : 'rgba(255, 255, 255, 0.2)',
                        backgroundColor: index === 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <CardActionArea onClick={() => handleSupplierSelect(supplier)}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                              <FormControlLabel
                                value={supplier.supplier}
                                control={<Radio />}
                                label=""
                                sx={{ margin: 0 }}
                              />
                              <Typography variant="h6" sx={{ color: 'white' }}>
                                {supplier.supplierName}
                                {index === 0 && (
                                  <Chip 
                                    label="Best Savings" 
                                    color="success" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                            </Box>
                            <Chip
                              label={formatPercentage(supplier.percentageDifference)}
                              color={getSavingsColor(supplier.percentageDifference)}
                              size="medium"
                            />
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={2}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Annual Cost
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#4fc3f7' }}>
                                {formatCurrency(supplier.annualExpenditure)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Monthly Cost
                              </Typography>
                              <Typography variant="body1" sx={{ color: 'white' }}>
                                {formatCurrency(supplier.monthlyExpenditure)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Standing Charge
                              </Typography>
                              <Typography variant="body1" sx={{ color: 'white' }}>
                                {supplier.standingCharge ? `${supplier.standingCharge}p/day` : 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Day Rate
                              </Typography>
                              <Typography variant="body1" sx={{ color: 'white' }}>
                                {supplier.dayUnitRate ? `${supplier.dayUnitRate}p/kWh` : 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Contract Period
                              </Typography>
                              <Typography variant="body1" sx={{ color: 'white' }}>
                                {supplier.contractPeriodMonths} months
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Annual Savings
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: (supplier.percentageDifference ?? 0) > 0 ? '#4caf50' : '#f44336'
                                }}
                              >
                                {quote.currentSupplier.annualExpenditure != null && supplier.annualExpenditure != null
                                  ? formatCurrency(
                                      quote.currentSupplier.annualExpenditure - supplier.annualExpenditure
                                    )
                                  : 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </Grid>

          {/* Comparison Table */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Quick Comparison
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Supplier</TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Annual Cost</TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Monthly Cost</TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Savings</TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>Contract Period</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Current: {quote.currentSupplier.supplierName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {formatCurrency(quote.currentSupplier.annualExpenditure)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {formatCurrency(quote.currentSupplier.monthlyExpenditure)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>-</TableCell>
                    <TableCell align="right" sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>-</TableCell>
                  </TableRow>
                  {sortedSuppliers.map((supplier, index) => (
                    <TableRow 
                      key={supplier.supplier}
                      sx={{
                        backgroundColor: selectedSupplierId === supplier.supplier ? 'rgba(33, 150, 243, 0.2)' : 'transparent',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        '& td': { 
                          fontWeight: selectedSupplierId === supplier.supplier ? 'bold' : 'normal',
                          color: 'white',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <TableCell>
                        {supplier.supplierName}
                        {index === 0 && (
                          <Chip 
                            label="Best" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(supplier.annualExpenditure)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(supplier.monthlyExpenditure)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatPercentage(supplier.percentageDifference)}
                          color={getSavingsColor(supplier.percentageDifference)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {supplier.contractPeriodMonths} months
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedSupplierId}
        >
          Select Supplier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierSelectionModal;
